import { Collapse } from 'antd';
import { useFormikContext } from 'formik';
import { getOr, isNil, keyBy } from 'lodash/fp';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../../../shared/permissions';
import { useCompany } from '../../../../../../components/contexts/CompanyContextManager';
import CollapsibleWrapper from '../../../../../../components/wrappers/CollapsibleWrapper';
import hasPermissions from '../../../../../../utilities/hasPermissions';
import {
    exportFinanceProductTableExcel,
    prepareFirstRow,
    prepareWorkbook,
} from '../../../../../../utilities/newVersionExcel';
import ExportExcel from '../../../shared/ExportExcel';
import ImportExcel from '../../../shared/ImportExcel';
import { type FinanceProductFormValues } from '../../index';
import EditableTable from '../EditableTable';
import useLeaseReferenceData from './useLeaseReferenceData';
import useTerms from './useTerms';

const { Panel } = Collapse;

const mappedHeaders = [
    { text: 'Variant', key: 'variantName' },
    { text: 'Reference*', key: 'variantId' },
    { text: 'Term*', key: 'term' },
];

const excelColumnKeys = mappedHeaders.map(i => i.text);

const getVariantAndTermKey = ({ variantSuiteId, term }) => `${variantSuiteId}-${term}`;

const LeaseReference = ({ disabled = false }: { disabled: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { variantColumns, finderVehicleColumns, variantRecords, finderVehicleRecords } = useLeaseReferenceData({
        isDisabled: disabled,
    });
    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const company = useCompany(true);
    const showFinderAssignment =
        company?.availableModules.hasFinderVehicleManagementModules &&
        hasPermissions(company?.permissions, [permissionKind.viewFinderVehicles]);

    const {
        displayName,
        lease: { table: leaseTable },
        term,
    } = values;

    const terms = useTerms(term);

    const onImport = useCallback(
        async data => {
            const currentRecords = [...variantRecords, ...finderVehicleRecords];
            const tableDataList = data
                .filter(
                    record =>
                        !isNil(record.value) &&
                        currentRecords.some(i => i.variantId === `${record.variantId}`) &&
                        terms.includes(record.term)
                )
                .map(value => ({
                    term: value.term,
                    variantSuiteId: currentRecords.find(i => i.variantId === `${value.variantId}`).variantSuiteId,
                    value: value.value,
                }));

            setFieldValue('lease.table', tableDataList);
        },
        [setFieldValue, variantRecords, finderVehicleRecords, terms]
    );

    const exportExcel = useCallback(async () => {
        const workbook = await prepareWorkbook();
        const worksheet = workbook.getWorksheet(1);

        prepareFirstRow(worksheet, excelColumnKeys, terms);

        const leaseTableDataByVariantAndTerm = keyBy(getVariantAndTermKey, leaseTable || []);
        [...variantRecords, ...finderVehicleRecords].forEach(row => {
            const { variantSuiteId, variantId, variantName } = row;
            const values = terms.map(term => {
                const variantAndTermKey = getVariantAndTermKey({ variantSuiteId, term });

                return getOr(null, `${variantAndTermKey}.value`, leaseTableDataByVariantAndTerm);
            });
            worksheet.addRow([variantName, variantId, ...values]);
        });

        exportFinanceProductTableExcel(workbook, displayName, 'Lease');
    }, [leaseTable, variantRecords, finderVehicleRecords, displayName, terms]);

    return (
        <CollapsibleWrapper defaultActiveKey={['leaseReference']}>
            <Panel key="leaseReference" header={t('financeProductDetails:tables.lease.title')}>
                <EditableTable columns={variantColumns} records={variantRecords} />
                {showFinderAssignment && (
                    <EditableTable columns={finderVehicleColumns} records={finderVehicleRecords} />
                )}
            </Panel>
            {!disabled && (
                <>
                    <ImportExcel
                        mappedHeaders={mappedHeaders}
                        onImport={onImport}
                        tableType="matrix"
                        title={t('financeProductDetails:importModal.titles.lease')}
                    />
                    <ExportExcel onClick={exportExcel} />
                </>
            )}
        </CollapsibleWrapper>
    );
};

export default LeaseReference;
